/**
 * SEO helpers for gardener pages
 */

export const isValidPricing = (pricing) => {
  if (!pricing) return false
  const min = pricing.minimum || pricing.min || pricing.from
  const max = pricing.maximum || pricing.max || pricing.to
  return min > 1 && max > 1 && min < max
}

export const getPricingValues = (pricing) => {
  if (!pricing) return null
  return {
    min: pricing.minimum || pricing.min || pricing.from || 0,
    max: pricing.maximum || pricing.max || pricing.to || 0
  }
}

export const formatPricingAfterTax = (price) => {
  const result = price / 2
  return result % 1 === 0 ? result.toString() : result.toFixed(2).replace('.', ',')
}

/** 4.857 -> "4,9" (French decimal separator, same convention as the prices). */
export const formatRating = (average) => {
  return (Math.round(average * 10) / 10).toFixed(1).replace('.', ',')
}

export const getVisibleServices = (proposedServices, maxVisible) => {
  maxVisible = maxVisible || 5
  if (!proposedServices || !proposedServices.length) return { visible: [], overflow: 0 }
  return {
    visible: proposedServices.slice(0, maxVisible),
    overflow: Math.max(0, proposedServices.length - maxVisible)
  }
}

export const buildGardenerMetaTitle = (name, city, postalCode, services) => {
  const servicePart = services && services.length > 0
    ? ' - ' + services.slice(0, 3).join(', ')
    : ''
  const location = postalCode
    ? city + ' (' + postalCode + ')'
    : city
  return name + ', jardinier à ' + location + servicePart + ' | CMonJardinier'
}

export const buildGardenerMetaDescription = (name, city, postalCode, services, experience) => {
  var parts = []
  const location = postalCode
    ? city + ' (' + postalCode + ')'
    : city
  parts.push('Découvrez le profil de ' + name + ', jardinier à ' + location + '.')
  if (experience) {
    parts.push(experience + ' ans d\'expérience.')
  }
  if (services && services.length > 0) {
    parts.push('Spécialités : ' + services.slice(0, 4).join(', ') + '.')
  }
  parts.push('Demandez un devis gratuit et bénéficiez de 50% de crédit d\'impôt.')
  return parts.join(' ')
}

export const buildGardenerJsonLd = (gardener, city, postalCode, externalId) => {
  if (!gardener) return null

  var pricing = getPricingValues(gardener.pricing)
  var pricingValid = isValidPricing(gardener.pricing)

  var schema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': 'https://www.cmonjardinier.com/app/jardinier/' + externalId,
    'name': (gardener.name || '').trim() + ' - Jardinier paysagiste',
    'description': buildGardenerMetaDescription(
      (gardener.name || '').trim(),
      city,
      postalCode,
      gardener.proposedServices,
      gardener.since
    ),
    'url': 'https://www.cmonjardinier.com/app/jardinier/' + externalId,
    'image': gardener.profilePicture ? gardener.profilePicture.path : undefined,
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': city,
      'postalCode': postalCode,
      'addressCountry': 'FR'
    },
    'areaServed': (gardener.zipCodes || []).map(function (zip) {
      return {
        '@type': 'AdministrativeArea',
        'name': zip + ', France'
      }
    }),
    'parentOrganization': {
      '@type': 'Organization',
      'name': 'CMonJardinier',
      'url': 'https://www.cmonjardinier.com'
    }
  }

  if (gardener.proposedServices && gardener.proposedServices.length > 0) {
    schema.knowsAbout = gardener.proposedServices
  }

  if (pricingValid && pricing) {
    schema.priceRange = pricing.min + '€ - ' + pricing.max + '€/h'
    schema.makesOffer = {
      '@type': 'Offer',
      'priceCurrency': 'EUR',
      'price': pricing.min + '-' + pricing.max,
      'description': 'Tarif horaire jardinage (avant crédit d\'impôt 50%)',
      'eligibleRegion': {
        '@type': 'Country',
        'name': 'FR'
      }
    }
  }

  return schema
}

export const buildListingJsonLd = (city, postalCode, gardeners) => {
  if (!city || !gardeners) return null

  var breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Accueil',
        'item': 'https://www.cmonjardinier.com'
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': 'Jardiniers à ' + city,
        'item': 'https://www.cmonjardinier.com/app/jardiniers/' + encodeURIComponent(city) + '/' + postalCode
      }
    ]
  }

  var itemList = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    'name': 'Jardiniers paysagistes à ' + city + ' (' + postalCode + ')',
    'numberOfItems': gardeners.length,
    'itemListElement': gardeners.slice(0, 20).map(function (g, i) {
      var name = (g.name || '').trim()
      var gardenerUrl = 'https://www.cmonjardinier.com/app/jardinier/' + (g.externalId || g.id)
      var itemData = {
        '@type': 'ProfessionalService',
        'name': name + ' - Jardinier paysagiste',
        'url': gardenerUrl,
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': city,
          'postalCode': postalCode,
          'addressCountry': 'FR'
        }
      }
      var pic = g.pictureFile || g.profilePicture
      if (pic && pic.path) {
        itemData.image = pic.path
      }
      if (g.proposedServices && g.proposedServices.length > 0) {
        itemData.knowsAbout = g.proposedServices.slice(0, 5)
      }
      var pricing = getPricingValues(g.pricing)
      if (isValidPricing(g.pricing) && pricing) {
        itemData.priceRange = pricing.min + '€ - ' + pricing.max + '€/h'
      }
      return {
        '@type': 'ListItem',
        'position': i + 1,
        'item': itemData
      }
    })
  }

  return [breadcrumb, itemList]
}

export const buildGardenerBreadcrumbJsonLd = (name, city, postalCode, externalId) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Accueil',
        'item': 'https://www.cmonjardinier.com'
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': 'Jardiniers à ' + city,
        'item': 'https://www.cmonjardinier.com/app/jardiniers/' + encodeURIComponent(city) + '/' + postalCode
      },
      {
        '@type': 'ListItem',
        'position': 3,
        'name': name,
        'item': 'https://www.cmonjardinier.com/app/jardinier/' + externalId
      }
    ]
  }
}

export const addRatingsToJsonLd = (schema, ratings) => {
  if (!schema || !ratings || !ratings.count) return schema
  schema.aggregateRating = {
    '@type': 'AggregateRating',
    'ratingValue': ratings.average.toFixed(1),
    'reviewCount': ratings.count,
    'bestRating': '5',
    'worstRating': '1'
  }
  return schema
}



// WEBPACK FOOTER //
// ./src/utilities/gardener-seo.js